// src/components/SPanel/Home/ActivityWidgets.jsx
//
// Widget-urile de activitate, din răspunsurile salvate cu data lor:
//  - „Activitate”: seria de zile la rând (1×1) sau harta zilelor în care a
//    exersat (2×1, 2×2), pe săptămâni, o singură nuanță (mai închis = mai mult);
//  - „Progres pe zile”: răspunsurile corecte / greșite pe zile (bare stivuite)
//    și, la 2×2, într-un grafic separat (altă scară), întrebările învățate.
// Atingerea / trecerea peste o zi o arată în rândul de dedesubt.

import React, { useMemo, useState } from "react";

import { CountUp, Head, Skeleton, Tile } from "./HomeTile";
import { useAppeared, useElementSize } from "./homeHooks";
import {
   activeDays,
   formatShortDay,
   heatLevel,
   learnedCurve,
   monthShort,
   niceMax,
   streaks,
   sumDays,
   weekColumns,
} from "./homeActivity";
import { roCount } from "./homeLessons";

import calendarIcon from "../../../assets/svg/mdi--calendar-outline.svg";
import chartIcon from "../../../assets/svg/mdi--chart-line.svg";

const WEEKDAY_LETTERS = ["L", "M", "M", "J", "V", "S", "D"];

function answersText(n) {
   return roCount(n, "răspuns", "răspunsuri");
}

function dayDetails(day) {
   if (!day) return "";
   if (!day.answers) return `${formatShortDay(day.date)}: n-ai exersat`;
   return `${formatShortDay(day.date)}: ${answersText(day.answers)}, ${day.correct} corecte`;
}

/* ================= Activitate ================= */

function WeekDots({ days }) {
   const last = days.slice(-7);
   return (
      <div className="wd-weekdots" aria-hidden="true">
         {last.map((day) => {
            const d = new Date(`${day.date}T00:00`);
            const letter = WEEKDAY_LETTERS[(d.getDay() + 6) % 7];
            return (
               <span key={day.date} className={day.answers > 0 ? "is-on" : ""}>
                  <i />
                  {letter}
               </span>
            );
         })}
      </div>
   );
}

function Heatmap({ days, labels, onActive, active }) {
   const [ref, { width, height }] = useElementSize();
   const max = useMemo(() => Math.max(0, ...days.map((day) => day.answers)), [days]);

   // toate săptămânile cu date, cât mai mari; dacă nu încap, cele mai recente
   const gap = 3;
   const left = labels ? 16 : 0;
   const top = labels ? 14 : 0;
   // câte săptămâni dau pătrățele cât mai mari pe locul dat (cel puțin 13)
   const MIN_CELL = 7;
   const maxWeeks = Math.ceil(days.length / 7);
   const avail = width - left + gap;
   const byHeight = Math.max(MIN_CELL, Math.floor((height - top - gap * 6) / 7));
   const fitting = Math.round(avail / (Math.min(26, byHeight) + gap));
   let weeks = Math.max(Math.min(13, maxWeeks), Math.min(maxWeeks, fitting));
   let cell = Math.min(26, byHeight, Math.floor(avail / weeks - gap));
   if (cell < MIN_CELL) {
      cell = MIN_CELL;
      weeks = Math.max(1, Math.floor(avail / (cell + gap)));
   }
   const columns = useMemo(() => weekColumns(days, weeks), [days, weeks]);
   const gridWidth = weeks * (cell + gap) - gap;
   const x0 = left + Math.max(0, (width - left - gridWidth) / 2);

   return (
      <div ref={ref} className="wd-heat" onPointerLeave={() => onActive(null)}>
         {width > 0 && height > 0 && (
            <svg width={width} height={top + 7 * cell + 6 * gap} role="img" aria-label="Zilele în care ai exersat">
               {labels &&
                  columns.map((column, w) => {
                     const first = column.cells.find(Boolean);
                     const prev = w > 0 ? columns[w - 1].cells.find(Boolean) : null;
                     if (!first) return null;
                     const month = new Date(`${first.date}T00:00`).getMonth();
                     if (prev && new Date(`${prev.date}T00:00`).getMonth() === month) return null;
                     return (
                        <text key={`m${w}`} className="wd-heat__label" x={x0 + w * (cell + gap)} y={10}>
                           {monthShort(new Date(`${first.date}T00:00`))}
                        </text>
                     );
                  })}
               {labels &&
                  [0, 2, 4].map((d) => (
                     <text key={`d${d}`} className="wd-heat__label" x={0} y={top + d * (cell + gap) + cell - 2}>
                        {WEEKDAY_LETTERS[d]}
                     </text>
                  ))}
               {columns.map((column, w) =>
                  column.cells.map((day, d) => {
                     if (!day) return null;
                     const level = heatLevel(day.answers, max);
                     return (
                        <rect
                           key={day.date}
                           className={
                              `wd-heat__cell wd-heat__cell--l${level}` +
                              (active?.date === day.date ? " is-active" : "")
                           }
                           x={x0 + w * (cell + gap)}
                           y={top + d * (cell + gap)}
                           width={cell}
                           height={cell}
                           rx={Math.max(2, cell * 0.28)}
                           onPointerEnter={() => onActive(day)}
                           onClick={() => onActive(day)}
                        />
                     );
                  }),
               )}
            </svg>
         )}
      </div>
   );
}

export function ActivityWidget({ size, activity }) {
   const [active, setActive] = useState(null);
   const days = activity.days;
   const { current, best } = useMemo(() => streaks(days), [days]);

   if (!activity.loaded) {
      return (
         <Tile id="activity" size={size}>
            <Head icon={calendarIcon} title="Activitate" />
            <Skeleton />
         </Tile>
      );
   }

   if (size === "1x1") {
      return (
         <Tile id="activity" size={size} to="/student/test" label={`Serie: ${current} zile la rând`}>
            <Head icon={calendarIcon} title="Serie" />
            <div className="wd-big">
               <span className="wd-big__num">
                  <CountUp value={current} />
               </span>
               <span className="wd-big__unit">{current === 1 ? "zi la rând" : "zile la rând"}</span>
            </div>
            <WeekDots days={days} />
         </Tile>
      );
   }

   const recent = days;
   const total = sumDays(recent);
   const weeksShown = Math.round(recent.length / 7);
   const summary = total.answers
      ? `${roCount(activeDays(recent), "zi activă", "zile active")} în ultimele ${roCount(weeksShown, "săptămână", "săptămâni")} · seria: ${current}`
      : "Încă nu ai exersat. Biletele te așteaptă la Testare.";

   return (
      <Tile id="activity" size={size}>
         <Head
            icon={calendarIcon}
            title="Activitate"
            action={size === "2x2" ? { to: "/student/test", label: "Exersează" } : null}
         />
         <Heatmap days={recent} labels={size === "2x2"} active={active} onActive={setActive} />
         {size === "2x2" && (
            <div className="wd-facts wd-facts--row">
               <div className="wd-fact">
                  <span>Seria</span>
                  <strong>
                     <CountUp value={current} />
                  </strong>
               </div>
               <div className="wd-fact">
                  <span>Cea mai lungă</span>
                  <strong>
                     <CountUp value={best} />
                  </strong>
               </div>
               <div className="wd-fact">
                  <span>Răspunsuri</span>
                  <strong>
                     <CountUp value={total.answers} />
                  </strong>
               </div>
            </div>
         )}
         <p className="wd-readout" aria-live="polite">
            {active ? dayDetails(active) : summary}
         </p>
      </Tile>
   );
}

/* ================= Progres pe zile ================= */

/** Dreptunghi cu colțurile de sus rotunjite (capătul barei), jos drept (pe axă). */
function topRoundedRect(x, y, w, h, r) {
   const radius = Math.max(0, Math.min(r, h, w / 2));
   return (
      `M${x},${y + h}` +
      `V${y + radius}` +
      `Q${x},${y} ${x + radius},${y}` +
      `H${x + w - radius}` +
      `Q${x + w},${y} ${x + w},${y + radius}` +
      `V${y + h}Z`
   );
}

function DailyBars({ days, activeIndex, onActive, grow }) {
   const [ref, { width, height }] = useElementSize();
   const top = 14;
   const bottom = 16;
   const plotH = Math.max(10, height - top - bottom);
   const slot = days.length ? width / days.length : 0;
   const barW = Math.max(3, Math.min(24, slot * 0.62));
   const max = niceMax(Math.max(0, ...days.map((day) => day.answers)));
   const scale = (value) => (grow ? (value / max) * plotH : 0);
   const baseY = top + plotH;

   return (
      <div ref={ref} className="wd-bars" onPointerLeave={() => onActive(null)}>
         {width > 0 && height > 0 && (
            <svg width={width} height={height} role="img" aria-label="Răspunsuri corecte și greșite pe zile">
               <line className="wd-chart__grid" x1={0} x2={width} y1={top + 0.5} y2={top + 0.5} />
               <text className="wd-chart__tick" x={0} y={top - 4}>
                  {max}
               </text>
               {days.map((day, i) => {
                  const x = i * slot + (slot - barW) / 2;
                  const okH = scale(day.correct);
                  const badH = scale(day.wrong);
                  // 2px de fundal între segmente; capătul de sus rotunjit
                  const gap = okH > 0 && badH > 0 ? 2 : 0;
                  const okTop = baseY - okH;
                  return (
                     <g key={day.date} className={activeIndex === i ? "is-active" : ""}>
                        {activeIndex === i && (
                           <rect className="wd-chart__band" x={i * slot} y={top} width={slot} height={plotH} rx={6} />
                        )}
                        {okH > 0 && (
                           <path
                              className="wd-chart__ok"
                              d={topRoundedRect(x, okTop, barW, okH, badH > 0 ? 0 : 4)}
                           />
                        )}
                        {badH > 0 && (
                           <path
                              className="wd-chart__bad"
                              d={topRoundedRect(x, okTop - gap - badH, barW, badH, 4)}
                           />
                        )}
                        <rect
                           className="wd-chart__hit"
                           x={i * slot}
                           y={0}
                           width={slot}
                           height={height}
                           onPointerEnter={() => onActive(i)}
                           onClick={() => onActive(i)}
                        />
                     </g>
                  );
               })}
               <line className="wd-chart__axis" x1={0} x2={width} y1={baseY + 0.5} y2={baseY + 0.5} />
               {days.length > 0 && (
                  <>
                     <text className="wd-chart__tick" x={0} y={height - 2}>
                        {formatShortDay(days[0].date).split(", ")[1]}
                     </text>
                     <text className="wd-chart__tick" x={width} y={height - 2} textAnchor="end">
                        azi
                     </text>
                  </>
               )}
            </svg>
         )}
      </div>
   );
}

function LearnedChart({ points, activeIndex, onActive, grow }) {
   const [ref, { width, height }] = useElementSize();
   if (!points.length) return <div ref={ref} className="wd-learned" />;

   const top = 8;
   const bottom = 6;
   const plotH = Math.max(10, height - top - bottom);
   const values = points.map((p) => p.total);
   const min = Math.min(...values);
   const max = Math.max(...values);
   const span = Math.max(1, max - min);
   const stepX = points.length > 1 ? (width - 8) / (points.length - 1) : 0;
   const xy = points.map((p, i) => [
      4 + i * stepX,
      top + plotH - ((p.total - min) / span) * plotH * (grow ? 1 : 0),
   ]);
   const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
   const area = `${line}L${xy[xy.length - 1][0]},${top + plotH}L${xy[0][0]},${top + plotH}Z`;
   const end = xy[xy.length - 1];
   const focus = activeIndex != null ? xy[activeIndex] : null;

   return (
      <div ref={ref} className="wd-learned" onPointerLeave={() => onActive(null)}>
         {width > 0 && height > 0 && (
            <svg width={width} height={height} role="img" aria-label="Întrebări învățate, cumulat">
               <path className="wd-chart__area" d={area} />
               <path className="wd-chart__line" d={line} />
               {focus && (
                  <line className="wd-chart__cross" x1={focus[0]} x2={focus[0]} y1={top} y2={top + plotH} />
               )}
               <circle className="wd-chart__dot" cx={(focus || end)[0]} cy={(focus || end)[1]} r={4} />
               {points.map((p, i) => (
                  <rect
                     key={p.date}
                     className="wd-chart__hit"
                     x={4 + i * stepX - stepX / 2}
                     y={0}
                     width={Math.max(stepX, 6)}
                     height={height}
                     onPointerEnter={() => onActive(i)}
                     onClick={() => onActive(i)}
                  />
               ))}
            </svg>
         )}
      </div>
   );
}

export function ProgressWidget({ size, activity }) {
   const [activeIndex, setActiveIndex] = useState(null);
   const grow = useAppeared(250);
   const [boxRef, { width }] = useElementSize();
   const count = Math.max(7, Math.min(30, Math.floor((width || 300) / 18)));
   const days = useMemo(() => activity.days.slice(-count), [activity.days, count]);
   const curve = useMemo(() => {
      const before = activity.days.slice(0, -count);
      const learnedBefore = activity.learnedBefore + before.reduce((s, d) => s + d.learned, 0);
      return learnedCurve(days, learnedBefore);
   }, [activity.days, activity.learnedBefore, days, count]);

   const total = sumDays(days);
   const active = activeIndex != null ? days[activeIndex] : null;
   const legend = (
      <span className="wd-legend" aria-hidden="true">
         <i className="wd-legend__ok" />
         corecte
         <i className="wd-legend__bad" />
         greșite
      </span>
   );

   let readout;
   if (active) {
      readout = `${formatShortDay(active.date)}: ${answersText(active.answers)} · ${active.correct} corecte · ${active.wrong} greșite`;
      if (size === "2x2") readout += ` · ${curve[activeIndex].total} învățate`;
   } else if (total.answers) {
      readout = `Ultimele ${days.length} zile: ${answersText(total.answers)}, ${Math.round((100 * total.correct) / total.answers)}% corecte`;
   } else {
      readout = `Niciun răspuns în ultimele ${days.length} zile.`;
   }

   return (
      <Tile id="progress" size={size}>
         <Head icon={chartIcon} title="Răspunsuri" extra={legend} />
         <div ref={boxRef} className="wd-progress">
            {!activity.loaded ? (
               <Skeleton />
            ) : (
               <>
                  <DailyBars days={days} activeIndex={activeIndex} onActive={setActiveIndex} grow={grow} />
                  {size === "2x2" && (
                     <div className="wd-progress__learned">
                        <div className="wd-progress__learnedHead">
                           <span>Întrebări învățate</span>
                           <strong>
                              <CountUp value={curve.length ? curve[curve.length - 1].total : 0} />
                           </strong>
                        </div>
                        <LearnedChart points={curve} activeIndex={activeIndex} onActive={setActiveIndex} grow={grow} />
                     </div>
                  )}
               </>
            )}
         </div>
         <p className="wd-readout" aria-live="polite">
            {readout}
         </p>
      </Tile>
   );
}
